/** Map https://app.bilenia.se or se.bilenia.app:// URLs to in-app hash routes (path + query). */
const HTTPS_HOSTS = new Set(["app.bilenia.se", "www.app.bilenia.se", "localhost"]);
const CUSTOM_SCHEMES = new Set(["se.bilenia.app:", "se.bilenia.auctions:"]);

export function parseExternalUrlToAppRoute(url: string): string | null {
  try {
    const parsed = new URL(url);
    const protocol = parsed.protocol.toLowerCase();

    // Custom scheme: se.bilenia.app://auth?bankid=success&nativeCode=…
    // URL parser puts "auth" in hostname and path segments in pathname.
    if (CUSTOM_SCHEMES.has(protocol)) {
      const hostPart = parsed.hostname || "";
      const pathPart =
        !parsed.pathname || parsed.pathname === "/" ? "" : parsed.pathname;
      const withPath = `/${hostPart}${pathPart}`.replace(/\/{2,}/g, "/");
      if (!withPath || withPath === "/") return null;
      return `${withPath}${parsed.search || ""}`;
    }

    const host = parsed.hostname.toLowerCase();
    if (!HTTPS_HOSTS.has(host)) return null;

    if (parsed.hash) {
      const hashRaw = parsed.hash.replace(/^#/, "");
      if (hashRaw.startsWith("/")) return hashRaw;
    }

    const path = parsed.pathname.replace(/\/+$/, "") || "/";
    if (path === "/" || path === "") return null;
    const withPath = path.startsWith("/") ? path : `/${path}`;
    return `${withPath}${parsed.search || ""}`;
  } catch {
    return null;
  }
}

/**
 * Universal link BankID opens after same-device auth on iOS
 * (`appswitch:resumeUrl`). It only brings the app to the foreground so the
 * ASWebAuthenticationSession can finish — it must not navigate or close the sheet.
 */
export const BANKID_RESUME_PATH = "/bankid-return";

export function isBankIdResumeUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (!HTTPS_HOSTS.has(parsed.hostname.toLowerCase())) return false;
    return parsed.pathname.replace(/\/+$/, "") === BANKID_RESUME_PATH;
  } catch {
    return false;
  }
}

/** Listing deep link: /listings/:slug or legacy /auctions/:campaignId/:listingId */
export function isListingAppRoute(route: string): boolean {
  const path = route.split("?")[0] || "";
  return (
    /^\/listings\/[^/]+$/.test(path) ||
    /^\/auctions\/[^/]+\/[^/]+$/.test(path)
  );
}

export function buildListingWebUrl(
  _campaignId: string,
  listingIdOrSlug: string,
  webBase?: string,
): string {
  const base =
    webBase?.replace(/\/+$/, "") ||
    (
      (import.meta.env.VITE_WEB_APP_URL as string | undefined)?.trim() ||
      "https://app.bilenia.se"
    ).replace(/\/+$/, "");
  return `${base}/listings/${listingIdOrSlug}`;
}

/** Custom-scheme URL that opens the native app (BankID return / "Open in app"). */
export function buildNativeAppAuthDeepLink(search: string): string {
  const qs = search.startsWith("?") ? search : search ? `?${search}` : "";
  return `se.bilenia.app://auth${qs}`;
}
